import type { VariantProps } from 'class-variance-authority';
import { cva } from 'class-variance-authority';

export type ButtonVariants = VariantProps<typeof buttonStyles>;

export const buttonStyles = cva(
  [
    'relative',
    'w-fit',
    'inline-flex items-center justify-center ',
    'font-medium',
    'rounded-md',
    'whitespace-nowrap',
    'no-underline',
    'focus-visible:ring-2 focus-visible:ring-border-active focus:outline-hidden',
    'transition-colors transition-shadow',
    'selection-none',
  ],
  {
    variants: {
      variant: {
        primary: [
          'bg-button-primary',
          'text-button-primary-foreground',
          'hover:bg-button-primary-hover hover:text-button-primary-foreground', // for <a>
          'enabled:hover:bg-button-primary-hover enabled:hover:text-button-primary-foreground', // for <button>
          'active:bg-button-primary-pressed active:text-button-primary-foreground', // for <a>
          'enabled:active:bg-button-primary-pressed enabled:active:text-button-primary-foreground', // for <button>
          'enabled:active:shadow-pressed',
          'disabled:bg-button-primary-disabled disabled:text-text-disabled',
          'group-hover:enabled:bg-button-primary-hover group-hover:enabled:text-button-primary-foreground',
        ],
        secondary: [
          'bg-button-secondary',
          'ring-1 ring-border-secondary',
          'text-text-primary',
          'hover:bg-button-secondary-hover hover:text-text-primary', // for <a>
          'enabled:hover:bg-button-secondary-hover enabled:hover:text-text-primary', // for <button>
          'active:bg-button-secondary-pressed active:text-text-primary', // for <a>
          'enabled:active:bg-button-secondary-pressed enabled:active:text-text-primary', // for <button>
          'disabled:bg-button-secondary-disabled disabled:text-text-disabled',
          'group-hover:enabled:bg-button-secondary-hover group-hover:enabled:text-text-primary',
        ],
        tertiary: [
          'bg-button-tertiary',
          'text-text-primary',
          'hover:bg-button-tertiary-hover hover:text-text-primary', // for <a>
          'enabled:hover:bg-button-tertiary-hover enabled:hover:text-text-primary', // for <button>
          'active:bg-button-tertiary-pressed active:text-text-primary', // for <a>
          'enabled:active:bg-button-tertiary-pressed enabled:active:text-text-primary', // for <button>
          'disabled:bg-button-tertiary-disabled disabled:text-text-disabled',
          'group-hover:enabled:bg-button-tertiary-hover group-hover:enabled:text-text-primary',
        ],
        transparent: [
          'bg-transparent',
          'text-text-primary',
          'hover:bg-transparent hover:text-text-primary', // for <a>
          'enabled:hover:bg-transparent enabled:hover:text-text-primary', // for <button>
          'active:bg-transparent active:text-text-primary', // for <a>
          'enabled:active:bg-transparent enabled:active:text-text-primary', // for <button>
          'disabled:bg-transparent disabled:text-text-disabled',
          'group-hover:enabled:bg-transparent group-hover:enabled:text-text-primary',
        ],
        ghost: [
          'bg-border-primary',
          'text-text-primary',
          'hover:bg-border-primary/80 hover:text-text-primary', // for <a>
          'enabled:hover:bg-border-primary/80 enabled:hover:text-text-primary', // for <button>
          'active:bg-border-primary/60 active:text-text-primary', // for <a>
          'enabled:active:bg-border-primary/60 enabled:active:text-text-primary', // for <button>
          'disabled:bg-border-primary/50 disabled:text-text-disabled',
          'group-hover:enabled:bg-border-primary/80 group-hover:enabled:text-text-primary',
        ],
        subtle: [
          'bg-surface-secondary',
          'text-text-secondary',
          'hover:bg-border-primary/50 hover:text-text-secondary', // for <a>
          'enabled:hover:bg-border-primary/50 enabled:hover:text-text-secondary', // for <button>
          'active:bg-gray-500 active:text-text-secondary', // for <a>
          'enabled:active:bg-gray-500 enabled:active:text-text-secondary', // for <button>
          'disabled:bg-surface-secondary/50 disabled:text-text-disabled',
          'group-hover:enabled:bg-border-primary/50 group-hover:enabled:text-text-secondary',
        ],
        danger: [
          'bg-red-100',
          'text-text-primary',
          'hover:bg-red-100/80 hover:text-text-primary', // for <a>
          'enabled:hover:bg-red-100/80 enabled:hover:text-text-primary', // for <button>
          'active:bg-red-100/60 active:text-text-primary', // for <a>
          'enabled:active:bg-red-100/60 enabled:active:text-text-primary', // for <button>
          'disabled:bg-red-100/50 disabled:text-text-disabled',
          'group-hover:enabled:bg-red-100/80 group-hover:enabled:text-text-primary',
        ],
      },
      size: {
        small: ['px-2', 'py-1.5', 'text-sm leading-4', 'gap-1'],
        medium: ['px-4', 'py-2', 'text-base', 'gap-2'],
        large: ['px-4', 'py-2', 'text-xl', 'gap-2'],
      },
    },
    defaultVariants: {
      variant: 'primary',
      size: 'medium',
    },
  },
);
